// All new components must import useTranslation and use t() for UI strings
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import useTranslation from '../../hooks/useTranslation';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, user, loading } = useAuth();
  const { t, setLanguage, language } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (!loading && user) {
      navigate('/project-detail', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
      
      if (error) {
        setError(error?.message || 'Invalid email or password');
        setIsSubmitting(false);
      } else if (data?.user) {
        // Navigation handled by useEffect
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Show loading state during initial auth check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-sm text-muted-foreground">{t('login.loadingAuth')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Helmet>
        <title>Login - OnSitePM</title>
      </Helmet>

      {/* Language Toggle — Top Right */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-1 px-1 py-1 rounded-full"
        style={{ background: 'rgba(255,107,44,0.08)', border: '1px solid rgba(255,107,44,0.2)' }}>
        <button
          type="button"
          onClick={() => setLanguage('en')}
          className="px-3 py-1 rounded-full text-xs font-semibold transition-all duration-150"
          style={language === 'en'
            ? { background: '#FF6B2C', color: '#FFFFFF' }
            : { background: 'transparent', color: '#FF6B2C' }}
        >
          EN
        </button>
        <button
          type="button"
          onClick={() => setLanguage('fr')}
          className="px-3 py-1 rounded-full text-xs font-semibold transition-all duration-150"
          style={language === 'fr'
            ? { background: '#FF6B2C', color: '#FFFFFF' }
            : { background: 'transparent', color: '#FF6B2C' }}
        >
          FR
        </button>
      </div>

      {/* LEFT PANEL — Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col items-center justify-center"
        style={{ background: 'linear-gradient(145deg, #1a2535 0%, #2C3E50 50%, #1e3a5f 100%)' }}>

        {/* Blueprint grid overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `
            linear-gradient(rgba(255,107,44,0.6) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,107,44,0.6) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px'
        }} />

        {/* Structural lines decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-left corner bracket */}
          <div className="absolute top-10 left-10 w-16 h-16 border-t-2 border-l-2 opacity-20" style={{ borderColor: '#FF6B2C' }} />
          {/* Bottom-right corner bracket */}
          <div className="absolute bottom-10 right-10 w-16 h-16 border-b-2 border-r-2 opacity-20" style={{ borderColor: '#FF6B2C' }} />
          {/* Diagonal accent line */}
          <div className="absolute top-0 right-0 w-px h-full opacity-10" style={{ background: 'linear-gradient(to bottom, transparent, #FF6B2C, transparent)' }} />
          {/* Building silhouette shapes */}
          <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center opacity-8">
            <div className="flex items-end gap-1">
              <div className="w-8 bg-white/5 rounded-t" style={{ height: '80px' }} />
              <div className="w-12 bg-white/5 rounded-t" style={{ height: '120px' }} />
              <div className="w-6 bg-white/5 rounded-t" style={{ height: '60px' }} />
              <div className="w-16 bg-white/5 rounded-t" style={{ height: '160px' }} />
              <div className="w-10 bg-white/5 rounded-t" style={{ height: '100px' }} />
              <div className="w-8 bg-white/5 rounded-t" style={{ height: '70px' }} />
              <div className="w-14 bg-white/5 rounded-t" style={{ height: '140px' }} />
              <div className="w-6 bg-white/5 rounded-t" style={{ height: '55px' }} />
            </div>
          </div>
        </div>

        {/* Centered branding content */}
        <div className="relative z-10 text-center px-12 max-w-md">
          {/* Logo mark */}
          <div className="flex items-center justify-center mb-8">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)', boxShadow: '0 8px 32px rgba(255,107,44,0.4)' }}>
                <Icon name="HardHat" size={36} color="#FFFFFF" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-md flex items-center justify-center"
                style={{ background: '#2C3E50', border: '2px solid #FF6B2C' }}>
                <Icon name="Building2" size={12} color="#FF6B2C" />
              </div>
            </div>
          </div>

          {/* Brand name */}
          <h1 className="text-5xl font-bold text-white mb-3 tracking-tight">
            OnSite<span style={{ color: '#FF6B2C' }}>PM</span><sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup>
          </h1>

          {/* Tagline — premium headline */}
          <p className="text-2xl font-semibold leading-snug mb-3 tracking-tight" style={{ color: 'rgba(255,255,255,0.92)', letterSpacing: '-0.01em' }}>
            {t('login.heroTagline')}
          </p>

          {/* Supporting text */}
          <p className="text-sm font-normal mt-1 mb-6" style={{ color: 'rgba(255,255,255,0.42)', letterSpacing: '0.01em', lineHeight: '1.6' }}>
            {t('login.heroSubtitle')}
          </p>

          {/* Ecosystem badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: 'rgba(255,107,44,0.12)', border: '1px solid rgba(255,107,44,0.25)' }}>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF6B2C' }} />
            <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {t('login.poweredBy')}
            </span>
          </div>

          {/* Operational intelligence micro-labels */}
          <div className="mt-10 flex flex-wrap justify-center gap-2">
            {[
              { icon: 'TrendingUp', label: 'Profitability' },
              { icon: 'BarChart2', label: 'Forecasting' },
              { icon: 'Settings2', label: 'Operations' },
              { icon: 'DollarSign', label: 'Budget Control' },
              { icon: 'FilePlus', label: 'Extras' },
              { icon: 'AlertTriangle', label: 'Risk Alerts' },
            ]?.map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}>
                <Icon name={icon} size={11} color="rgba(255,107,44,0.75)" />
                <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.02em' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* RIGHT PANEL — Login Card */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md">

          {/* Mobile logo (shown only on small screens) */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)' }}>
              <Icon name="HardHat" size={22} color="#FFFFFF" />
            </div>
            <div className="ml-3">
              <span className="text-2xl font-bold text-foreground">OnSite<span style={{ color: '#FF6B2C' }}>PM</span><sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup></span>
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-card rounded-2xl border border-border p-8 shadow-subtle">

            {/* Card header */}
            <div className="mb-8">
              {/* Desktop logo inside card */}
              <div className="hidden lg:flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #FF6B2C 0%, #E55A1F 100%)' }}>
                  <Icon name="HardHat" size={16} color="#FFFFFF" />
                </div>
                <span className="text-lg font-bold text-foreground">OnSite<span style={{ color: '#FF6B2C' }}>PM</span><sup style={{ fontSize: '0.45em', verticalAlign: 'super', lineHeight: 0, color: '#FF6B2C' }}>™</sup></span>
              </div>

              <h2 className="text-2xl font-semibold text-foreground">{t('login.signInTitle')}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t('login.signInSubtitle')}</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t('login.emailLabel')}
                type="email"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                placeholder={t('login.emailPlaceholder')}
                required
                autoComplete="email"
                className="focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />

              <div className="relative">
                <Input
                  label={t('login.passwordLabel')}
                  type={showPassword ? 'text' : 'password'}
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  placeholder={t('login.passwordPlaceholder')}
                  required
                  autoComplete="current-password"
                  className="focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-150"
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end -mt-1">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-xs font-medium hover:underline"
                  style={{ color: '#FF6B2C' }}
                >
                  Forgot password?
                </button>
              </div>

              {error && (
                <div className="flex items-center space-x-2 p-3 rounded-lg bg-red-50 border border-red-200">
                  <Icon name="AlertCircle" size={16} className="text-red-600" />
                  <span className="text-sm text-red-600">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || !formData?.email || !formData?.password}
                className="w-full flex items-center justify-center space-x-2"
                style={
                  !(isSubmitting || !formData?.email || !formData?.password)
                    ? { backgroundColor: '#FF6B2C', color: '#FFFFFF' }
                    : undefined
                }
                onMouseEnter={(e) => {
                  if (!(isSubmitting || !formData?.email || !formData?.password)) {
                    e.currentTarget.style.backgroundColor = '#E85D1F';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!(isSubmitting || !formData?.email || !formData?.password)) {
                    e.currentTarget.style.backgroundColor = '#FF6B2C';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <Icon name="Loader2" size={18} className="animate-spin" />
                    <span>{t('login.signingIn')}</span>
                  </>
                ) : (
                  <>
                    <Icon name="LogIn" size={18} />
                    <span>{t('login.signInButton')}</span>
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs font-semibold text-foreground">OnSitePM<sup style={{ fontSize: '0.55em', verticalAlign: 'super', lineHeight: 0 }}>™</sup></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;